/**
 * Onboarding Check Component
 * Redirects users to onboarding if they haven't completed it
 * 
 * Uses the OnboardingStateMachine as SINGLE SOURCE OF TRUTH.
 * NEVER navigates during hydration or active tour.
 * 
 * Logging: All decisions go through flight recorder.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { isTourActiveGlobal, isTourOrTransitioning } from '@/hooks/useTourController';
import { 
  logFlight,
  canNavigate,
  getOnboardingState,
  isHydrated
} from '@/hooks/useOnboardingStateMachine';
import { diagLog } from '@/utils/diagnosticSession';

interface OnboardingCheckProps {
  children: React.ReactNode;
}

// Session cache to prevent redundant DB queries after onboarding completion
let onboardingCompletedCache: { [userId: string]: boolean } = {};

export const OnboardingCheck: React.FC<OnboardingCheckProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      const state = getOnboardingState();
      const hydrated = isHydrated();
      
      // Get localStorage flags for diagnostics
      const welcomeSeen = localStorage.getItem('therappi_welcome_seen_/') === 'true';
      const tourSeen = localStorage.getItem('therappi-first-time-tour-seen') === 'true';
      
      const diagnosticData = {
        file: 'OnboardingCheck',
        path: location.pathname,
        state,
        hydrated,
        tourActive: isTourActiveGlobal(),
        tourOrTransitioning: isTourOrTransitioning(),
        welcomeSeen,
        tourSeen
      };
      
      logFlight('CHECK_START', diagnosticData);

      // RULE: Skip all checks if tour/transition is active
      if (isTourOrTransitioning()) {
        logFlight('CHECK_BLOCKED_TOUR', { ...diagnosticData, decision: 'blocked_tour_or_transitioning' });
        setIsChecking(false);
        return;
      }

      // RULE: Don't check during hydration
      if (!hydrated) {
        logFlight('CHECK_BLOCKED_HYDRATING', { ...diagnosticData, decision: 'blocked_not_hydrated' });
        // Don't navigate, just wait
        setIsChecking(false);
        return;
      }

      // Skip check if already on onboarding page
      if (location.pathname === '/onboarding') {
        logFlight('CHECK_SKIP_ON_ONBOARDING_PAGE', { ...diagnosticData, decision: 'already_on_onboarding' });
        setIsChecking(false);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          logFlight('CHECK_NO_USER', { ...diagnosticData, decision: 'no_user' });
          setIsChecking(false);
          return;
        }

        // Check session cache first
        if (onboardingCompletedCache[user.id] === true) {
          logFlight('CHECK_CACHED_COMPLETE', { ...diagnosticData, userId: user.id, decision: 'cached_complete' });
          setIsChecking(false);
          return;
        }

        // Double-check tour state before DB query
        if (isTourOrTransitioning()) {
          logFlight('CHECK_BLOCKED_TOUR_MID', { ...diagnosticData, decision: 'blocked_tour_mid_check' });
          setIsChecking(false);
          return;
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', user.id)
          .single();

        const onboardingCompleted = profile?.onboarding_completed ?? false;
        
        // Update diagnostic data with DB result
        const postDbDiagnostic = {
          ...diagnosticData,
          userId: user.id,
          onboarding_completed: onboardingCompleted,
          state: getOnboardingState(), // Re-check state after DB call
          tourActive: isTourActiveGlobal(),
          tourOrTransitioning: isTourOrTransitioning()
        };
        
        logFlight('CHECK_DB_RESULT', postDbDiagnostic);

        if (onboardingCompleted) {
          // Cache the result
          onboardingCompletedCache[user.id] = true;
          logFlight('CHECK_COMPLETE_CACHED', { ...postDbDiagnostic, decision: 'marking_cached' });
          setIsChecking(false);
        } else {
          // Triple-check before navigating
          if (!canNavigate(location.pathname, '/onboarding', 'onboarding_check')) {
            logFlight('CHECK_NAV_BLOCKED', { 
              ...postDbDiagnostic,
              fromPath: location.pathname, 
              toPath: '/onboarding',
              reason: 'canNavigate returned false',
              decision: 'nav_blocked_by_guard'
            });
            setIsChecking(false);
            return;
          }
          
          // ======= DIAGNOSTIC: LOG BEFORE NAVIGATE =======
          logFlight('CHECK_REDIRECTING_TO_ONBOARDING', { 
            ...postDbDiagnostic,
            fromPath: location.pathname, 
            toPath: '/onboarding',
            reason: 'onboarding_not_completed',
            decision: 'NAVIGATING_NOW'
          });
          
          // NOTE: Do NOT call clearFirstRunFlags() here!
          // Pre-auth flags (welcome_seen, typewriter_seen) must persist
          // to prevent the 4-box welcome from reappearing after auth.
          
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        logFlight('CHECK_ERROR', { ...diagnosticData, error: String(error) });
        setIsChecking(false);
      }
    };

    checkOnboardingStatus();
  }, [navigate, location.pathname]);

  if (isChecking) {
    // Don't show spinner if tour is active or transitioning
    if (isTourOrTransitioning()) {
      return <>{children}</>;
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
};

// Export a function to mark onboarding as complete in the cache
export const markOnboardingCompletedInCache = (userId: string) => {
  logFlight('CACHE_MARKED_COMPLETE', { userId });
  onboardingCompletedCache[userId] = true;
};

// Export a function to clear the cache (for logout)
export const clearOnboardingCache = () => {
  logFlight('CACHE_CLEARED', {});
  onboardingCompletedCache = {};
};
